import * as RT from '@radix-ui/react-tooltip';
import type { ReactNode } from 'react';

export interface TooltipProps {
  content: ReactNode;
  children: ReactNode;
  side?: 'top' | 'right' | 'bottom' | 'left';
  /** Extra viewport padding used by Radix's built-in collision detection,
   *  so the tooltip stays inside the visible area even when the trigger
   *  is near a screen edge. Increase for wider tooltip content. */
  collisionPadding?: number;
  /**
   * Render our own `<span>` around the trigger instead of merging props onto
   * the child.
   *
   * NEEDED FOR DISABLED CONTROLS, and this is not a style preference. A
   * disabled `<button>` emits no pointer events, so `asChild` hands Radix a
   * trigger that can never fire and the tooltip silently never opens. The
   * native `title` attribute DOES show on a disabled element, so converting a
   * disabled button's `title` to a plain Tooltip is a straight regression --
   * and it lands on exactly the tooltips that matter most, the ones explaining
   * why a control is unavailable.
   *
   * The wrapper is `inline-flex` so it does not disturb the layout of the
   * control it wraps.
   */
  wrap?: boolean;
}

/**
 * The one hover in this app.
 *
 * Anything that explains something on hover goes through here rather than
 * through the native `title` attribute. `title` renders as the browser's own
 * tooltip -- white box, dark text, ~1s delay, no styling hook, no control over
 * placement, and it ignores the app's theme entirely, so a dark-mode page grows
 * a bright white rectangle. Mixing the two also means two different delays and
 * two different looks for the same gesture on the same screen.
 *
 * `title` is still correct for a few things that are NOT hovers: our own
 * components' `title` PROPS (Card, PageHeader, FilterPanel, Modal), and the
 * validation message on a constrained `<input>`, which the browser shows
 * itself.
 */
export function Tooltip({
  content,
  children,
  side = 'top',
  collisionPadding = 8,
  wrap = false,
}: TooltipProps) {
  return (
    <RT.Provider delayDuration={150}>
      <RT.Root>
        {wrap ? (
          <RT.Trigger asChild>
            <span className="tooltip-wrap">{children}</span>
          </RT.Trigger>
        ) : (
          <RT.Trigger asChild>{children}</RT.Trigger>
        )}
        <RT.Portal>
          <RT.Content
            className="tooltip"
            side={side}
            sideOffset={6}
            collisionPadding={collisionPadding}
          >
            {content}
          </RT.Content>
        </RT.Portal>
      </RT.Root>
    </RT.Provider>
  );
}

/**
 * A label with a hover definition: dotted underline, help cursor, styled panel.
 *
 * This pattern was written out by hand at ~20 call sites -- column headers,
 * stat labels, inline glossary terms -- each repeating the same border and
 * cursor styles inline, and several of them reaching for a raw `title` instead.
 * One component means they cannot drift apart again.
 */
export function InfoLabel({
  children,
  definition,
  side = 'top',
  maxWidth = 320,
}: {
  children: ReactNode;
  definition: ReactNode;
  side?: TooltipProps['side'];
  maxWidth?: number;
}) {
  return (
    <Tooltip
      side={side}
      collisionPadding={12}
      content={<span style={{ maxWidth, display: 'block' }}>{definition}</span>}
    >
      <span className="info-label">{children}</span>
    </Tooltip>
  );
}
